import React from "react";
import styled from "@emotion/styled";
import { IconButton, Menu, MenuItem } from "@mui/material";
import { Link } from "react-router-dom";
import { UserRoles } from "@store/auth.store";
import { RoleAuthorized } from "@shared/components/RoleAuthorized";
import { UserAvatar } from "../../UserAvatar";
import { useDevTools } from "@features/DevTools/useDevTools";

type Props = {
  onLogout: () => void;
  onSettingsClick: () => void;
  userFirstName: string;
  userName: string;
  userEmail: string;
  userRoles: Array<string>;
  userAvatar: string | null;
};

const AvatarButton = styled(IconButton)`
  padding: 0;
  color: inherit;
`;

export const UserAvatarMenu = (props: Props) => {
  const {
    onLogout,
    onSettingsClick,
    userFirstName,
    userEmail,
    userRoles,
    userAvatar,
  } = props;

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const { isDevToolsEnabled, handleToggleDevTools } = useDevTools();

  const handleClickMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const getHandleClick = (handler: () => void) => () => {
    handler();
    handleClose();
  };

  return (
    <>
      <AvatarButton
        aria-controls={open ? "user-avatar-menu" : undefined}
        aria-haspopup="true"
        aria-expanded={open ? "true" : undefined}
        onClick={handleClickMenu}
        sx={{
          borderRadius: "50%",
        }}
      >
        <UserAvatar
          userAvatar={userAvatar}
          userName={userFirstNameOrEmail(userFirstName, userEmail)}
        />
      </AvatarButton>

      <Menu
        id="user-avatar-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        autoFocus={false}
        MenuListProps={{ "aria-labelledby": "user-avatar-menu" }}
      >
        <RoleAuthorized
          userRoles={userRoles}
          authorizedRoles={[UserRoles.Admin]}
          authorizedComponent={
            <MenuItem component={Link} to={"/admin"}>
              Admin
            </MenuItem>
          }
        />
        <MenuItem component={Link} to={"/account/profile"}>
          Profile
        </MenuItem>
        <MenuItem component={Link} to={"/account"}>
          Account
        </MenuItem>
        <MenuItem onClick={getHandleClick(onSettingsClick)}>Settings</MenuItem>
        {isDevToolsEnabled && (
          <MenuItem onClick={getHandleClick(() => handleToggleDevTools(true))}>
            DevTools
          </MenuItem>
        )}
        <MenuItem onClick={getHandleClick(onLogout)}>Sign Out</MenuItem>
      </Menu>
    </>
  );
};

const userFirstNameOrEmail = (userName: string, userEmail: string) => {
  // Prefer a name when present; the avatar internally derives initials from this.
  return userName?.trim()?.length ? userName : userEmail;
};
